import { useEffect, useMemo, useRef, useState } from 'react';
import { BriefcaseBusiness, CheckCircle2, Loader2, Mail, UserCheck, Users, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LeadCaptureModal from '../../components/LeadCaptureModal';
import TalentDiagnosticsSummary from '../../components/TalentDiagnosticsSummary';
import { analyzeTalentDiagnostics, getPublicTalentDiagnosticsDefinitions, getPublicTalentDiagnosticsDefinitionById, type TalentDiagnosticsDefinition, type TalentDiagnosticsQuestion, type TalentDiagnosticsSection, DiagnosticType } from '../../api/talentDiagnosticsApi';
import { getTalentDiagnosticsProgressMessage, getTalentDiagnosticsQuestionPosition, normalizeTalentDiagnosticsAnalysisResponse, normalizeTalentDiagnosticsDefinitionPayload, parseTalentDiagnosticsOptionJson } from '../../utils/talentDiagnosticsPublic';
import { normalizeDefinitionId } from '../../utils/talentDiagnostics';
import strivreLogo from "../../assets/images/strivre-logo.svg";
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../../components/RecaptchaCheckbox';

interface TalentDiagnosticsAnswerMap {
  [key: string]: string;
}

interface TalentDiagnosticsAnalysisResponse {
  requiresLeadCapture?: boolean;
  leadCaptureId?: string | null;
  report?: any;
  benchmark?: {
    name?: string;
    description?: string;
    recommendationContent?: string;
  };
  sectionResults?: Array<{
    name?: string;
    score?: number;
    weighting?: number;
    isSkipped?: boolean;
  }>;
  strengths?: string[];
  improvementOpportunities?: string[];
  recommendations?: Array<{ title?: string; content?: string }>;
  scoringSnapshotJson?: string;
  summaryReport?: string[];
  overallPercentage?: number;
  visualIndicator?: 'excellent' | 'neutral' | 'poor' | 'critical';
}

export default function TalentDiagnosticsTool() {
  const [, setDefinitions] = useState<TalentDiagnosticsDefinition[]>([]);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string>('');
  const [sections, setSections] = useState<TalentDiagnosticsSection[]>([]);
  const [questions, setQuestions] = useState<TalentDiagnosticsQuestion[]>([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<TalentDiagnosticsAnswerMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(true);
  const [requiresLeadCapture, setRequiresLeadCapture] = useState(false);
  const [, setPendingAnswers] = useState<TalentDiagnosticsAnswerMap>({});
  const [, setLeadCaptureId] = useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<TalentDiagnosticsAnalysisResponse | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [,setIsSubmissionComplete] = useState(false);
  const navigate = useNavigate();

  const { token } = useAuth();
  const isAuthenticated = !!token;

  const pageWrapperClass = isAuthenticated
    ? 'min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0'
    : 'min-h-screen px-4 sm:px-6 lg:px-8 text-slate-100 mb-20';

  useEffect(() => {
    const loadDefinitions = async () => {
      try {
        const data = await getPublicTalentDiagnosticsDefinitions();
        const activeDefinitions = (data || []).filter((item) => item.isActive !== false);
        setDefinitions(activeDefinitions);
        if (activeDefinitions.length > 0) {
          const preferredDefinition = activeDefinitions.find((definition) => {
            const name = (definition.name || '').toLowerCase();
            return name.includes('talent acquisition diagnostic') || name.includes('talent acquisition');
          });

          const selectedDefinition = preferredDefinition || activeDefinitions[0];
          const nextId = normalizeDefinitionId(selectedDefinition.id);
          setSelectedDefinitionId(nextId);
        }
      } catch (err) {
        console.error('Failed to load talent diagnostics definitions:', err);
        setError('Failed to load the assessment definitions. Please try again.');
      } finally {
        setIsLoadingDefinitions(false);
      }
    };

    loadDefinitions();
  }, []);

  useEffect(() => {
    const loadSectionsAndQuestions = async () => {
      if (!selectedDefinitionId) {
        return;
      }

      try {
        const definition = await getPublicTalentDiagnosticsDefinitionById(selectedDefinitionId);
        const normalizedDefinition = normalizeTalentDiagnosticsDefinitionPayload(definition as Record<string, unknown>);
        const nextSections = (definition.sections || []) as TalentDiagnosticsSection[];
        const nextQuestions = normalizedDefinition.sections.flatMap((section) =>
          section.questions.map((question) => ({
            ...question,
            sectionId: question.sectionId || section.id,
            text: question.text || 'Untitled question',
          }))
        );

        setSections(nextSections.map((section, index) => ({
          ...section,
          name: section.name || normalizedDefinition.sections[index]?.name || `Section ${index + 1}`,
          description: section.description || normalizedDefinition.sections[index]?.description || '',
        })));
        setQuestions(nextQuestions as TalentDiagnosticsQuestion[]);
        setActiveSectionIndex(0);
      } catch (err) {
        console.error('Failed to load talent diagnostics structure:', err);
        setError('Failed to load the assessment questions. Please try again.');
      }
    };

    loadSectionsAndQuestions();
  }, [selectedDefinitionId]);

  const sectionHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sectionHeaderRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [activeSectionIndex]);

  const activeSection = sections[activeSectionIndex] ?? null;
  const sectionQuestions = useMemo(() => {
    if (!activeSection?.id) {
      return [] as TalentDiagnosticsQuestion[];
    }

    return questions.filter((question) => question.sectionId === activeSection.id);
  }, [activeSection?.id, questions]);

  const answeredQuestions = questions.filter((question) => {
    const answerKey =
      question.key || question.id || '';

    if (!answerKey) {
      return false;
    }

    const answer = answers[answerKey];

    return (
      answer !== undefined &&
      answer !== null &&
      String(answer).trim() !== ''
    );
  }).length;

  const completionPercentage =
    questions.length === 0
      ? 0
      : Math.round(
          (answeredQuestions / questions.length) * 100
        );

  const currentQuestionPosition =
    getTalentDiagnosticsQuestionPosition({
      activeSectionIndex,
      sections,
      questions,
    });

  const progressMessage =
    getTalentDiagnosticsProgressMessage(
      completionPercentage
    );

  const updateAnswer = (question: TalentDiagnosticsQuestion, value: string) => {
    const answerKey = question.key || question.id || '';
    if (!answerKey) {
      return;
    }

    setAnswers((prev) => ({ ...prev, [answerKey]: value }));
  };

  const handleSectionChange = (direction: 'next' | 'prev', event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();

    if (direction === 'next' && activeSectionIndex < sections.length - 1) {
      setActiveSectionIndex((prev) => prev + 1);
      return;
    }

    if (direction === 'prev' && activeSectionIndex > 0) {
      setActiveSectionIndex((prev) => prev - 1);
    }
  };

  const resetState = () => {
    setRequiresLeadCapture(false);
    setLeadCaptured(false);
    setCapturedEmail(null);
    setLeadCaptureId(null);
    setReport(null);
    setError(null);
    setPendingAnswers({});
    setAnswers({});
    setActiveSectionIndex(0);
    setIsSubmissionComplete(false);
  };

  const handleSubmit = async (event?: React.FormEvent, leadCaptureData?: { name: string; company: string; email: string }, leadCaptchaToken?: string) => {
    event?.preventDefault();

    if (!sections.length || activeSectionIndex < sections.length - 1) {
      return null;
    }

    const requestCaptchaToken = leadCaptureData ? leadCaptchaToken : captchaToken;
    if ((isAuthenticated || leadCaptureData) && !requestCaptchaToken) {
      setError('Please complete the CAPTCHA verification.');
      return null;
    }

    setIsLoading(true);
    setError(null);
    setPendingAnswers(answers);

    try {
      const data = await analyzeTalentDiagnostics({
        diagnosticDefinitionId: selectedDefinitionId,
        answers,
        diagnosticType: DiagnosticType.Talent,
        ...(requestCaptchaToken ? { captchaToken: requestCaptchaToken } : {}),
        ...(leadCaptureData ? {
          name: leadCaptureData.name,
          company: leadCaptureData.company,
          email: leadCaptureData.email,
        } : {}),
      });

      const normalizedAnalysis = normalizeTalentDiagnosticsAnalysisResponse(data as Record<string, unknown>);

      if (!isAuthenticated && data.requiresLeadCapture) {
        setRequiresLeadCapture(true);
        setReport(null);
        setLeadCaptured(false);
        setIsSubmissionComplete(false);
        return data;
      }

      setReport(normalizedAnalysis);
      setRequiresLeadCapture(false);
      setLeadCaptured(false);
      setLeadCaptureId(data.leadCaptureId ?? null);
      setIsSubmissionComplete(true);

      return normalizedAnalysis;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run diagnostics. Please try again.';
      console.error('Error running talent diagnostics:', errorMessage);
      setError(errorMessage);
      if (leadCaptureData) {
        throw err;
      }
      return null;
    } finally {
      setIsLoading(false);
      if (isAuthenticated) {
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      }
    }
  };

  const handleLeadCaptureSubmit = async (data: { name: string; company: string; email: string }, captchaToken: string) => {
    setIsLoading(true);
    try {
      const responseData = await handleSubmit(undefined, data, captchaToken);
      if (responseData) {
        setRequiresLeadCapture(false);
        setLeadCaptured(true);
        setLeadCaptureId(responseData.leadCaptureId ?? null);
        setCapturedEmail(data.email);
        setIsSubmissionComplete(true);
      } else {
        throw new Error('No report was returned after lead capture.');
      }
    } catch (err) {
      console.error('Error submitting lead capture:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit lead capture. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const renderQuestionInput = (question: TalentDiagnosticsQuestion) => {
    const answerKey = question.key || question.id || '';
    const currentValue = answers[answerKey] || '';
    const options = parseTalentDiagnosticsOptionJson(question.optionsJson);

    if (question.type?.toLowerCase() === 'multiselect') {
      return (
        <div className="space-y-3">
          {options.length > 0 ? options.map((option) => (
            <label
              key={option.key}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-all ${
                String(currentValue).split(',').includes(option.key)
                  ? 'border-strivre-burgundy bg-strivre-lightburgundy text-slate-900'
                  : 'border-strivre-lightnavy bg-slate-50 text-slate-700'
              }`}
            >
              <input
                type="checkbox"
                checked={String(currentValue).split(',').includes(option.key)}
                onChange={(event) => {
                  const selected = String(currentValue).split(',').filter(Boolean);
                  if (event.target.checked) {
                    selected.push(option.key);
                  } else {
                    const nextValues = selected.filter((item) => item !== option.key);
                    selected.splice(0, selected.length, ...nextValues);
                  }
                  updateAnswer(question, selected.join(','));
                }}
                className="h-4 w-4 rounded border-slate-300 text-strivre-burgundy focus:ring-violet-500"
              />
              <span>{option.text}</span>
            </label>
          )) : <p className="text-sm text-slate-500">No options configured for this question.</p>}
        </div>
      );
    }

    if (question.type?.toLowerCase() === 'boolean') {
      return (
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => updateAnswer(question, 'true')} className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-all ${currentValue === 'true' ? 'border-strivre-burgundy bg-strivre-burgundy text-white' : 'border-strivre-lightnavy bg-white text-slate-700'}`}>
            Yes
          </button>
          <button type="button" onClick={() => updateAnswer(question, 'false')} className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-all ${currentValue === 'false' ? 'border-strivre-burgundy bg-strivre-burgundy text-white' : 'border-strivre-lightnavy bg-white text-slate-700'}`}>
            No
          </button>
        </div>
      );
    }

    if (question.type?.toLowerCase() === 'rating') {
      return (
        <div className="grid grid-cols-1 gap-2">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => updateAnswer(question, option.key)}
              className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all ${
                currentValue === option.key
                  ? 'border-strivre-burgundy bg-strivre-burgundy text-white shadow-md'
                  : 'border-strivre-lightnavy bg-white text-slate-700 hover:border-strivre-burgundy hover:bg-strivre-lightburgundy'
              }`}
            >
              {option.text}
            </button>
          ))}
        </div>
      );
    }

    if (question.type?.toLowerCase() === 'text') {
      return (
        <textarea
          value={currentValue}
          onChange={(event) => updateAnswer(question, event.target.value)}
          className="input-base min-h-[140px] px-3 focus:ring-offset-0"
          placeholder="Add your answer"
        />
      );
    }

    if (
      ['number', 'percentage', 'currency'].includes(
        question.type?.toLowerCase() ?? ''
      )
    ) {
      return (
        <input
          type="number"
          value={currentValue}
          min={
            question.type?.toLowerCase() === 'percentage'
              ? 0
              : undefined
          }
          max={
            question.type?.toLowerCase() === 'percentage'
              ? 100
              : undefined
          }
          onChange={(event) =>
            updateAnswer(question, event.target.value)
          }
          className="input-base px-3 focus:ring-offset-0"
        />
      );
    }


    return (
      <div className="space-y-2">
        <select
          value={currentValue}
          onChange={(event) => updateAnswer(question, event.target.value)}
          className="input-base px-3 focus:ring-offset-0"
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.text}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const benchmark = useMemo(() => {
    if (!report?.scoringSnapshotJson) return null;

    const scoringSnapshot = JSON.parse(
        report.scoringSnapshotJson
    );

    const recommendationJson =
        scoringSnapshot?.MatchedBenchmark?.RecommendationJson;

    if (!recommendationJson) return null;

    const details = JSON.parse(recommendationJson);

    return {
        name: scoringSnapshot.MatchedBenchmark.Name,
        description: details.summary,
        recommendationContent:
        details.focusAreas?.join(', '),
    };
  }, [report?.scoringSnapshotJson]);

  return (
    <div className={pageWrapperClass}>
      <LeadCaptureModal
        isOpen={!isAuthenticated && requiresLeadCapture}
        onClose={resetState}
        onSubmit={handleLeadCaptureSubmit}
        loading={isLoading}
        websiteUrl="your talent acquisition diagnostics"
        title="Get Your Talent Acquisition Diagnostics Report"
        description="We need a few details so we can send your full report and follow up with tailored recommendations."
        companyLabel="Company"
      />

      {!isAuthenticated ? (
        <header className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-strivre-navy text-white border-b border-strivre-lightnavy shadow-lg mb-12">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="bg-white rounded-xl px-2">
                  <img
                    src={strivreLogo}
                    alt="Strivre"
                    className="h-16 w-auto object-contain"
                    />
                </div>
              </div>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/diagnostics/website-tool" className="text-white/70 hover:text-white transition-colors">Web Diagnostics</Link>
              <Link to="/diagnostics/marketing-tool" className="text-white/70 hover:text-white transition-colors">Marketing Diagnostics</Link>
              <Link to="/diagnostics/business-assessment" className="text-white/70 hover:text-white transition-colors">Business Diagnostics</Link>
              <Link to="/diagnostics/talent-diagnostics" className="text-white font-semibold">Talent Diagnostics</Link>
              <Link to="/diagnostics/recruitment-diagnostics" className="text-white/70 hover:text-white transition-colors">Recruitment Diagnostics</Link>
            </nav>

            <div className="flex items-center gap-3">
              <button className="btn-primary btn-md shrink-0" onClick={() => navigate('/login')}>Login</button>
              {/* <button className="btn-secondary btn-md shrink-0" onClick={() => navigate('/register')}>Sign Up</button> */}
            </div>
          </div>
        </header>
      ) : (
        <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Zap className="text-strivre-burgundy" size={24} />

              {report && !isLoading
                ? "Diagnostic Results"
                : "Talent Diagnostics"}
            </h1>

            <p className="text-slate-500 text-sm mt-1 text-left">
              {report && !isLoading
                ? "Review your talent acquisition maturity and strategic recommendations below."
                : "Answer a few questions to evaluate your Talent Acquisition function."}
            </p>
          </div>
        </header>
      )}
      {isLoadingDefinitions ? (
        <div className="flex flex-col items-center justify-center py-20 animate-pulse">
          <Loader2 className="w-12 h-12 text-strivre-burgundy animate-spin mb-4" />
          <p className="text-slate-500 font-medium text-lg">Loading your assessment framework...</p>
        </div>
      ) : null}

      {!report && !isLoading && !requiresLeadCapture && !isLoadingDefinitions && (
        <div className={!isAuthenticated ? "max-w-7xl mx-auto mb-8 animate-fade-in" : "w-full mb-8 animate-fade-in"}>
          
          {!leadCaptured && (
            <div className="mx-auto mb-8 animate-fade-in">
              <div className="rounded-3xl border border-strivre-lightnavy bg-white p-8 shadow-sm">

                <div className="text-center max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 rounded-full bg-strivre-lightburgundy px-4 py-2 text-sm font-semibold text-strivre-burgundy mb-4">
                    <Users size={16} />
                    Free Talent Assessment
                  </div>

                  <h2 className="text-4xl font-bold text-slate-900 mb-4">
                    How Mature Is Your Talent Acquisition Function?
                  </h2>

                  <p className="text-lg text-slate-600">
                    Evaluate your recruitment processes, candidate experience,
                    workforce planning, and hiring effectiveness to uncover
                    opportunities for attracting and retaining top talent.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mt-8">
                  <div className="rounded-2xl border border-slate-200 p-5">
                    <Users className="text-strivre-burgundy mb-3" size={22} />
                    <h3 className="font-semibold text-slate-900 mb-2">
                      Recruitment Process
                    </h3>
                    <p className="text-sm text-slate-600">
                      Assess the efficiency and consistency of your hiring workflows.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-5">
                    <UserCheck className="text-strivre-burgundy mb-3" size={22} />
                    <h3 className="font-semibold text-slate-900 mb-2">
                      Candidate Experience
                    </h3>
                    <p className="text-sm text-slate-600">
                      Understand how your hiring process is perceived by applicants.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-5">
                    <BriefcaseBusiness className="text-strivre-burgundy mb-3" size={22} />
                    <h3 className="font-semibold text-slate-900 mb-2">
                      Workforce Strategy
                    </h3>
                    <p className="text-sm text-slate-600">
                      Review workforce planning, talent pipelines, and hiring readiness.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div ref={sectionHeaderRef} className="mb-6 rounded-3xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 text-left">{activeSection?.name || 'Assessment'}</p>
                <h3 className="text-lg font-semibold text-slate-900 text-left">{activeSection?.description || 'Answer the questions below to build a complete picture of your Talent Acquisition performance.'}</h3>
              </div>
              <div className="text-sm text-slate-500">
                <div>
                  Question {currentQuestionPosition} of {questions.length || 0}
                </div>

                <div>
                  {answeredQuestions} of {questions.length} answered ·{" "}
                  {completionPercentage}% Complete
                </div>
              </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-strivre-softgrey">
              <div className="h-2 rounded-full bg-strivre-burgundy transition-all" style={{ width: `${completionPercentage}%` }} />
            </div>
            <div className="mt-4 rounded-2xl border border-strivre-lightburgundy bg-strivre-lightburgundy p-4 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">{progressMessage.heading}</p>
              <p className="mt-1">{progressMessage.body}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {sectionQuestions.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {sectionQuestions.map((question, index) => (
                  <div key={question.id || `${question.key}-${index}`} className="rounded-3xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 text-left">{activeSection?.name}</p>
                        <h4 className="text-lg font-semibold text-slate-900">{question.text}</h4>
                        {question.description ? <p className="mt-2 text-sm text-slate-600">{question.description}</p> : null}
                      </div>
                      <span className="rounded-full border border-strivre-lightnavy bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{index + 1}</span>
                    </div>
                    <div className="mt-5">{renderQuestionInput(question)}</div>
                    <p className="mt-4 text-sm text-slate-500">If this question is not applicable to your organisation, you may leave it unanswered.</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl border border-strivre-lightnavy bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
                There are no questions for the current section yet. Please select another definition or return later.
              </div>
            )}
            {activeSectionIndex >= sections.length - 1 && (
              // <div className={isAuthenticated ? "w-full" : "max-w-4xl mx-auto"}>
                <div className="rounded-2xl border border-strivre-lightnavy bg-strivre-lightburgundy p-4">
                  <p className="mb-2 text-sm font-semibold text-strivre-navy">
                    Privacy Notice
                  </p>

                  <p className="text-sm text-slate-700 leading-relaxed">
                    By submitting this form, you confirm that the information provided is
                    accurate and agree that Strivre Services and/or the relevant organisation
                    may contact you regarding your enquiry, request, or submitted information.
                    Your information will be handled in accordance with our Privacy Policy and
                    will not be used for purposes unrelated to your enquiry without your
                    consent.
                  </p>
                {/* </div> */}
              </div>
            )}
            {isAuthenticated && activeSectionIndex >= sections.length - 1 && (
              <div>
                <RecaptchaCheckbox ref={recaptchaRef} onChange={(nextToken) => { setCaptchaToken(nextToken); setError(null); }} disabled={isLoading} />
              </div>
            )}
            <div className="flex flex-wrap justify-between gap-3 pt-4">
              <button type="button" onClick={(event) => handleSectionChange('prev', event)} disabled={activeSectionIndex === 0} className="btn-secondary btn-md shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">Previous Section</button>
              {activeSectionIndex < sections.length - 1 ? (
                <button type="button" onClick={(event) => handleSectionChange('next', event)} className="btn-primary btn-md shrink-0">Next Section</button>
              ) : (
                <button type="submit" className="btn-primary btn-md shrink-0" disabled={isLoading}>Submit Assessment</button>
              )}
            </div>
          </form>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 animate-pulse">
          <Loader2 className="w-12 h-12 text-strivre-burgundy animate-spin mb-4" />
          <p className="text-slate-500 font-medium text-lg">Analyzing your Talent Acquisition performance...</p>
        </div>
      )}

      {report && !isLoading && !requiresLeadCapture && (
        <>
          {!isAuthenticated && leadCaptured ? (
            <div className="max-w-2xl mx-auto mb-12 animate-fade-in">
              <div className="bg-gradient-to-br from-strivre-softgrey to-white rounded-3xl border-2 border-strivre-lightnavy p-12 text-center shadow-lg">

                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 bg-strivre-burgundy rounded-full flex items-center justify-center">
                      <CheckCircle2 size={40} className="text-white" />
                    </div>

                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-strivre-navy rounded-full flex items-center justify-center border-4 border-white">
                      <Mail size={16} className="text-white" />
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Report Sent to Your Email!
                </h2>

                <p className="text-slate-700 text-lg mb-2">
                  We've sent your detailed talent diagnostic report to:
                </p>

                <p className="text-strivre-burgundy font-semibold text-lg mb-8 break-all">
                  {capturedEmail}
                </p>

                <div className="space-y-3 text-left text-slate-700 mb-8 max-w-md mx-auto">

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      Check your inbox for your complete talent diagnostic report
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      The report includes talent acquisition maturity insights and improvement opportunities
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      Our consultants may contact you to discuss recruitment and workforce strategy recommendations
                    </p>
                  </div>

                </div>

                <p className="text-sm text-slate-600 mb-6">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    onClick={resetState}
                    className="text-strivre-burgundy hover:text-red-900 font-semibold underline"
                  >
                    try another email
                  </button>
                </p>

                <button
                  onClick={resetState}
                  className="inline-block px-8 py-3 bg-strivre-burgundy text-white font-bold rounded-full hover:bg-red-900 transition-colors"
                >
                  Run Another Diagnostic
                </button>

              </div>
            </div>
          ) : (
            <div className="mt-8">
              <TalentDiagnosticsSummary
                overallPercentage={report.overallPercentage ?? 0}
                visualIndicator={report.visualIndicator || 'neutral'}
                benchmarkName={benchmark?.name}
                benchmarkDescription={benchmark?.description}
                benchmarkRecommendationContent={benchmark?.recommendationContent}
                sectionResults={report.sectionResults || []}
                strengths={report.strengths || []}
                improvementOpportunities={report.improvementOpportunities || []}
                recommendations={report.recommendations || []}
                diagnosticStats={report.scoringSnapshotJson ? JSON.parse(report.scoringSnapshotJson) : {}}
                report={report.summaryReport || []}
              />
            </div>
          )}
        </>
      )}

      {!isAuthenticated && (
        <footer className="fixed bottom-0 left-0 w-full bg-strivre-navy border-t border-strivre-lightnavy py-4 z-40">
          <div className="max-w-7xl mx-auto px-6 text-center text-sm text-slate-500">
            © 2026 Strivre. All rights reserved.
          </div>
        </footer>
      )}
    </div>
  );
}
